/**
 * Authenticated Layout Route
 *
 * Pathless layout route that gates all child routes behind Clerk authentication.
 * If the user is not signed in, they are redirected to /login.
 * If Clerk is still loading, renders nothing (child routes show their own spinners).
 *
 * Child routes:
 * - /no-access (authenticated, no workspace needed)
 * - /setup-pending (authenticated, waiting for workspace creation)
 * - /excel/authorize (authenticated, no workspace needed)
 * - /_withWorkspace/* (authenticated + workspace resolved)
 */

import { useEffect } from 'react';
import { createFileRoute, Navigate, Outlet, useLocation } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';

import { useWorkspaceStore } from '@/store/useWorkspaceStore';
import { queryClient } from '@/lib/queryClient';

export const Route = createFileRoute('/_authenticated')({
  component: AuthenticatedLayout,
});

function AuthenticatedLayout() {
  const { isLoaded, isSignedIn } = useAuth();
  const location = useLocation();
  const clearSelectedWorkspace = useWorkspaceStore((s) => s.clearSelectedWorkspace);

  // Clear all session state when user is not signed in (logout cleanup).
  // This also clears the TanStack Query cache so a subsequent login
  // doesn't briefly serve the previous user's data.
  useEffect(() => {
    if (isLoaded && !isSignedIn) {
      clearSelectedWorkspace();
      queryClient.clear();
    }
  }, [isLoaded, isSignedIn, clearSelectedWorkspace]);

  // While Clerk loads, render nothing. Child routes (_withWorkspace, setup-pending)
  // show their own loading spinners, so this avoids a double-spinner flash.
  if (!isLoaded) {
    return null;
  }

  if (!isSignedIn) {
    // Capture the current path so we can redirect back after login.
    // Exclude /login paths to prevent redirect loops — during route transitions,
    // useLocation() can briefly reflect the navigation target (/login) before
    // the layout unmounts, which would nest /login inside the from param.
    const fullPath = location.pathname + (location.searchStr || '');
    const from = location.pathname.startsWith('/login') ? undefined : fullPath;
    return <Navigate to="/login" search={{ from }} replace />;
  }

  return <Outlet />;
}
